// ─── List Detail Route (canonical friendly URL) ───
// `/app/l/{slug}-{short}` — ADR 0012 (Phase 6.5 item 2). Renders the
// ViewSwitcher, TaskFilterBar, and the active view (list / board / table /
// gantt / calendar). Gantt and Calendar are lazy-loaded for code splitting
// since they carry heavier deps.
//
// The loader resolves the friendly ref to a real list (scope-checked, D2) and
// self-heals a stale slug via a canonical redirect (D3) BEFORE this component
// ever renders — a logged-out cold load never reaches this loader; the `/app`
// guard (app.tsx) redirects to `/login?returnTo=<friendly url>` first via the
// existing, unmodified `safeReturnTo` mechanism (see the ADR's SSO sequence).

import { Suspense, lazy, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link2 } from 'lucide-react';
import { History } from 'lucide-react';
import { parseEntityRef, spacePath } from '@a8-planner/shared/utils/entity-url';
import type { ResolvedEntity } from '@a8-planner/shared';
import { useUIStore, recentContext } from '../../../stores/ui.store';
import { ListView } from '../../../features/tasks/ListView';
import { BoardView } from '../../../features/board/BoardView';
import { TableView } from '../../../features/table/TableView';
import { ViewSwitcher } from '../../../features/views/ViewSwitcher';
import { TaskExportMenu } from '../../../features/tasks/TaskExportMenu';
import { CreateAlertButton } from '../../../features/alerts/CreateAlertButton';
import { Breadcrumb, type BreadcrumbSegment } from '../../../features/layout/Breadcrumb';
import { TaskFilterBar } from '../../../features/filters/TaskFilterBar';
import { hasExplicitStatusFilter } from '../../../features/tasks/finishedWork';
import { useTaskFilters, listIdSearchSchema } from '../../../features/filters/useTaskFilters';
import type { ListSearchRecord } from '../../../features/filters/useTaskFilters';
import { ListScopeProvider } from '../../../features/saved-views/ListScopeContext';
import { TaskDetailPanel } from '../../../features/tasks/TaskDetailPanel';
import { RecentlyDeletedDialog } from '../../../features/tasks/RecentlyDeletedDialog';
import { DataverseTaskBulkImportDialog } from '../../../features/tasks/DataverseTaskBulkImportDialog';
import { useCanManageSpace, useCanWriteSpace } from '../../../hooks/useCanManageSpace';
import { ListTaskCreateProvider } from '../../../features/tasks/ListTaskCreateProvider';
import { CreateTaskButton } from '../../../features/tasks/CreateTaskButton';
import { listsApi } from '../../../lib/api/lists';
import { spacesApi } from '../../../lib/api/spaces';
import { workspaceApi } from '../../../lib/api/workspaces';
import { foldersApi } from '../../../lib/api/folders';
import { resolveEntity } from '../../../lib/entity-resolve';

// ─── Lazy-loaded views (code splitting) ───
// Split for their OWN size, not for third-party weight — neither view pulls a
// heavy library any more. @svar-ui/react-gantt was the original reason and is
// gone (renderer deleted in #26, last traces in GHE #734); the calendar is
// hand-rolled on deps the app already ships. What justifies the boundary now:
// Gantt 5.8 + WorkloadTimeline 13.4 KB gz, Calendar 5.1 + its year grid 9.1 KB gz.
// Re-measure before removing it. The Suspense boundary below shows a skeleton
// while the chunk loads.

const GanttView = lazy(() =>
  import('../../../features/gantt/GanttView').then((m) => ({
    default: m.GanttView,
  })),
);

const CalendarView = lazy(() =>
  import('../../../features/calendar/CalendarView').then((m) => ({
    default: m.CalendarView,
  })),
);

// ─── Route definition ───
// validateSearch registers the filter params so TanStack Router parses them
// from the URL and makes them available via useSearch(). Unknown keys are
// stripped; all fields are optional strings (coercion is the server's job).
//
// loader — ADR 0012 D3 parse → resolve → canonical-redirect-on-slug-drift flow:
//   1. parseEntityRef($listRef) → { slug, short }. Malformed → notFound().
//   2. GET /resolve?type=list&short&slug via resolveEntity (404 → notFound()).
//   3. If the URL's slug has drifted from the server's canonicalSlug (a rename),
//      redirect to the fresh canonicalPath, preserving every search param
//      (`search: (prev) => prev` — D5, so ?view=/?openTask=/filters survive).
//   4. Otherwise return the resolved entity — the component reads
//      listId/spaceId/workspaceId from it instead of route params.

export const Route = createFileRoute('/app/l/$listRef')({
  validateSearch: listIdSearchSchema,
  loader: async ({ params }): Promise<ResolvedEntity> => {
    const parsed = parseEntityRef(params.listRef);
    if (!parsed) {
      throw notFound();
    }

    const resolved = await resolveEntity('list', parsed.short, parsed.slug);

    if (parsed.slug !== resolved.canonicalSlug) {
      throw redirect({
        to: resolved.canonicalPath as '/app',
        search: (prev) => prev,
        replace: true,
      });
    }

    return resolved;
  },
  component: ListDetailPage,
});

// ─── Lazy view skeleton fallback ───

function ViewLoadingFallback(): ReactElement {
  return (
    <div className="flex flex-col gap-2 p-4" aria-busy="true" aria-label="Loading view">
      {/* Mimic a few skeleton rows so the layout does not jump */}
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex h-10 items-center gap-3 px-3">
          <div className="skeleton h-4 w-4 rounded" />
          <div className="skeleton h-4 flex-1 rounded" />
          <div className="skeleton hidden h-4 w-20 rounded sm:block" />
          <div className="skeleton hidden h-4 w-16 rounded md:block" />
        </div>
      ))}
    </div>
  );
}

// ─── Page component ───

function ListDetailPage(): ReactElement {
  const { listId, spaceId, workspaceId } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  // Array form (not just 'tasks') so the typed `t()` also accepts the
  // `common:breadcrumb.*` fallback-name keys used below (GHE #283 item 2).
  const { t } = useTranslation(['tasks', 'common']);

  // Filter state — single source for all 5 views, stored in URL search params.
  const filterHook = useTaskFilters(listId ?? '');
  const { apiParams, groupBy, viewMode, setViewMode } = filterHook;

  // GHE #284 item 1 — `viewMode` above is resolved SYNCHRONOUSLY (URL, falling
  // back to this list's persisted localStorage preference) and is what this
  // component renders from below. `setActiveView` here is a one-way MIRROR
  // into the Zustand store purely for the other, non-rendering consumers that
  // still read it (useAppliedView's `isModified` comparison, SavedViewsMenu's
  // "current state" save payload) — it never gates which view renders, so it
  // can safely lag by one render tick with no user-visible effect.
  const setActiveView = useUIStore((s) => s.setActiveView);
  useEffect(() => {
    setActiveView(viewMode);
  }, [viewMode, setActiveView]);

  // GHE #319 — declare this page's sidebar context, the same idiom `/app/s/$spaceRef` uses
  // for spaces. The space highlight is store-driven, but nothing updated it when a LIST page
  // opened, so whichever space was selected last stayed marked; and the list row's old
  // `params.listId` check died silently when this friendly URL became canonical (the only
  // param here is `listRef` — ids live in loader data). This route is the one place that
  // reliably knows both ids, however the user navigated (sidebar, Recents, Favorites,
  // search, deep link). The list highlight is cleared on unmount so it mirrors "open right
  // now", never "last visited"; the space highlight deliberately follows the existing
  // $spaceRef semantics (left set, cleared by company switch).
  const setActiveSpaceId = useUIStore((s) => s.setActiveSpaceId);
  const setActiveListId = useUIStore((s) => s.setActiveListId);
  useEffect(() => {
    if (spaceId) setActiveSpaceId(spaceId);
    if (listId) setActiveListId(listId);
    return (): void => {
      setActiveListId(null);
    };
  }, [spaceId, listId, setActiveSpaceId, setActiveListId]);

  // Recently-deleted / trash view (#95 cluster C) — MANAGE-gated, mirroring the
  // `GET /lists/{listId}/deleted-tasks` endpoint's own gate exactly.
  const canManageSpace = useCanManageSpace(workspaceId, spaceId);
  // GHE #597 — the create affordance is gated on WRITE for THIS space, not the
  // global `useCanWrite()` hint the views use. The route already resolved both
  // ids, and this mirrors the server's own `roleCan(_, 'write')`, so a VIEWER on
  // this space no longer sees a button whose submit would 403.
  const canWriteSpace = useCanWriteSpace(workspaceId, spaceId);
  // ─── Trabajo terminado (GHE #888, subido aquí en #1123) ───
  // El interruptor se pinta en la barra de filtros y su valor decide qué le pide la Lista al
  // servidor (`excludeDone`), así que el estado tiene que vivir por encima de las dos. Vivía en
  // `ListView`, que es por qué el botón se pintaba en una fila propia pegada a sus filas.
  const [showDone, setShowDone] = useState(false);
  const [recentlyDeletedOpen, setRecentlyDeletedOpen] = useState(false);
  const [dataverseImportOpen, setDataverseImportOpen] = useState(false);

  // ─── Deep-link: auto-open a task from Global Search / a notification (GHE #106 item 2) ───
  // `openTask` (+ its `comment` companion, item 1) used to only be consumed by the List
  // view (via the `initialOpenTaskId`/`initialCommentId` props below) — Board/Gantt/Table/
  // Calendar silently ignored it, so the deep-link degraded to "navigated to the right
  // list, panel never opens" whenever a non-List view was active
  // (live-verified regression, docs/audits/regression-audit-wave2-20260714.md §3).
  // Fixed by hoisting the panel-open decision to THIS route, one level above every view:
  // captured into local state (below), then rendered as an extra TaskDetailPanel instance
  // whenever the CURRENT `viewMode` isn't List (which keeps its own existing, working
  // internal open/close + row-click flow, so the two never double-render). This needed no
  // changes to Board/Gantt (DnD Specialist-owned) or Table (other-agent-owned this wave) —
  // the whole point of doing it at the route level instead of per-view.
  //
  // GHE #106 review panel FIX 1 (HIGH, 3-0, still needed): capture used to be a boolean
  // "consumed" latch, set once and NEVER reset. TanStack Router does not remount this route
  // component on search-param (or even path-param) changes, so every deep-link AFTER THE
  // FIRST was silently dropped while the route stayed mounted — open task A via a
  // notification, land correctly; click a SECOND notification (even to a different task)
  // and the param gets stripped but the panel never opens. Fixed by comparing against the
  // last-consumed VALUE (`lastConsumedOpenTaskRef`/`lastConsumedCommentRef`) instead of a
  // boolean: a genuinely new openTask id, or the same task with a new comment id,
  // re-captures; a still-in-flight strip-navigate re-delivering the identical pair does not
  // re-fire pointlessly. Reset on explicit close (`clearRouteDeepLink` below) so re-clicking
  // the SAME notification later still reopens.
  //
  // GHE #106 panel FIX 2 (MEDIUM) no longer applies, and its ownership-handoff machinery
  // (a `listOwnedTaskId` tracked via `onDeepLinkConsumed` + a settle-cleanup effect) has
  // been REMOVED (GHE #284 item 1): FIX 2 existed to survive a specific race — `viewMode`
  // used to come from a Zustand store that always STARTED at its default ('list') on a
  // fresh mount, even when this list's own persisted preference was something else, with
  // the correction landing one commit later. In that transient commit, ListView briefly
  // mounted and could claim a deep-link it was about to lose when `viewMode` corrected to
  // the real view — hence the handoff dance. `viewMode` (from useTaskFilters, above) is now
  // resolved SYNCHRONOUSLY from the URL/localStorage on the very first render — there is no
  // more transient wrong value, so gating capture directly on the CURRENT `viewMode` is
  // sufficient: when `viewMode === 'list'`, this effect simply never captures for the route
  // (ListView owns opening its own panel via its own internal, unrelated capture effect);
  // when it doesn't, the route captures exactly as before. No handoff needed either
  // direction.
  const lastConsumedOpenTaskRef = useRef<string | undefined>(undefined);
  const lastConsumedCommentRef = useRef<string | undefined>(undefined);
  const [routeDeepLinkTaskId, setRouteDeepLinkTaskId] = useState<string | null>(null);
  const [routeDeepLinkCommentId, setRouteDeepLinkCommentId] = useState<string | null>(null);

  useEffect(() => {
    if (
      viewMode !== 'list' &&
      search.openTask &&
      (search.openTask !== lastConsumedOpenTaskRef.current ||
        search.comment !== lastConsumedCommentRef.current)
    ) {
      lastConsumedOpenTaskRef.current = search.openTask;
      lastConsumedCommentRef.current = search.comment;
      setRouteDeepLinkTaskId(search.openTask);
      setRouteDeepLinkCommentId(search.comment ?? null);
    }
  }, [viewMode, search.openTask, search.comment]);

  // ListView reads `openTask`/`comment` via the `initialOpenTaskId`/`initialCommentId`
  // props below; here we strip both from the URL right after (for every view — this runs
  // regardless of which branch above fired) so a reload / back-navigation doesn't reopen
  // the panel. See useNavigateToSearchResult.ts and
  // features/notifications/useNotificationNavigate.ts for the two producers.
  useEffect(() => {
    if (search.openTask || search.comment) {
      void navigate({
        search: (prev: ListSearchRecord) => {
          const next = { ...prev };
          delete next.openTask;
          delete next.comment;
          return next;
        },
        replace: true,
      });
    }
  }, [search.openTask, search.comment, navigate]);

  // Shared by the route-level panel's own close button — releases both the rendered
  // state and the last-consumed-value tracking so a LATER re-click of the same
  // notification/search result reopens it instead of being ignored as "already
  // consumed" (FIX 1).
  const clearRouteDeepLink = (): void => {
    setRouteDeepLinkTaskId(null);
    setRouteDeepLinkCommentId(null);
    lastConsumedOpenTaskRef.current = undefined;
    lastConsumedCommentRef.current = undefined;
  };

  const handleCloseRouteDeepLinkPanel = (): void => {
    clearRouteDeepLink();
  };

  // Fetch list details (breadcrumb + statuses for filter bar + status filter)
  const { data: listData } = useQuery({
    queryKey: ['lists', listId],
    queryFn: () => listsApi.getById(listId ?? ''),
    enabled: !!listId,
  });

  // GHE #319 — second half of the sidebar-context declaration above: the containing FOLDER
  // is only known once the list details resolve (loader data carries listId/spaceId but not
  // folderId). Null when the list sits directly in its space. FolderNode uses this to reveal
  // itself on deep links, one-way, exactly like SpaceNode does with activeSpaceId.
  const setActiveListFolderId = useUIStore((s) => s.setActiveListFolderId);
  const listFolderId = listData?.data.folderId ?? null;
  useEffect(() => {
    setActiveListFolderId(listFolderId);
    return (): void => {
      setActiveListFolderId(null);
    };
  }, [listFolderId, setActiveListFolderId]);

  // Fetch space details for breadcrumb
  const { data: spaceData } = useQuery({
    queryKey: ['spaces', spaceId],
    queryFn: () => spacesApi.getById(spaceId),
    enabled: !!spaceId,
  });

  // Fetch workspace details for the breadcrumb (GHE #283 item 2 — the
  // breadcrumb used to show only "Space > List", despite its own header
  // comment promising "Workspace > Space > Folder > List").
  const { data: workspaceData } = useQuery({
    queryKey: ['workspaces', workspaceId],
    queryFn: () => workspaceApi.getById(workspaceId),
    enabled: !!workspaceId,
  });

  // A list is at most one folder deep (Space > Folder > List — Folder has no
  // `parentFolderId`, so there is no deeper chain to walk); `folderId` comes
  // from the list itself (already fetched above for statuses), not from
  // ResolvedEntity. Disabled entirely when the list sits directly under its
  // space (folderId null) — most real lists ARE folder-nested (see #96.2),
  // which is exactly why two same-named lists in different folders were
  // indistinguishable before this fix.
  const folderId = listData?.data?.folderId ?? null;
  const { data: folderData } = useQuery({
    queryKey: ['folders', folderId],
    queryFn: () => foldersApi.getById(folderId ?? ''),
    enabled: !!folderId,
  });

  const listName = listData?.data?.name ?? 'List';
  const spaceName = spaceData?.data?.name ?? 'Space';
  const workspaceName = workspaceData?.data?.name ?? t('common:breadcrumb.workspaceFallbackName');
  const folderName = folderData?.data?.name ?? t('common:breadcrumb.folderFallbackName');

  // #185 — record the visit in Recents once the real list name has loaded (never the fallback).
  // #325 — carry "Workspace · Space" alongside it: list names repeat across spaces, and Recents
  // renders with zero lookups, so the location has to be captured here or not at all. Built from
  // the RAW query values, never the display fallbacks above — persisting "Workspace · Space"
  // placeholders would be worse than showing nothing.
  const pushRecent = useUIStore((s) => s.pushRecent);
  const rawSpaceName = spaceData?.data?.name;
  const rawWorkspaceName = workspaceData?.data?.name;
  useEffect(() => {
    const name = listData?.data?.name;
    if (!listId || !name) return;
    pushRecent({
      type: 'LIST',
      id: listId,
      name,
      // SPACE only — the workspace is deliberately dropped (owner call, #325). Real tenants run
      // a single workspace with many spaces (Software Factory → Finance / Data / PP), so it is
      // the one part every entry shares: including it spent the whole subtitle on
      // "Software Factory · …" and truncated away the space, which is the only part that tells
      // two same-named lists apart.
      context: recentContext(rawSpaceName),
    });
  }, [listId, listData?.data?.name, rawWorkspaceName, rawSpaceName, pushRecent]);
  // Statuses come from the list query — already fetched for breadcrumb, no extra network request.
  const statuses = useMemo(() => listData?.data?.statuses ?? [], [listData]);

  const breadcrumbSegments: BreadcrumbSegment[] = [
    { label: workspaceName },
    { label: spaceName, href: spacePath(spaceName, spaceId) },
    ...(folderId ? [{ label: folderName }] : []),
    { label: listName },
  ];

  return (
    // GHE #105: carries this list's already-resolved workspaceId/spaceId down to
    // SavedViewsMenu (rendered inside TaskFilterBar) via Context rather than new
    // props threaded through TaskFilterBar's own signature — see
    // ListScopeContext.tsx for why.
    <ListScopeProvider workspaceId={workspaceId} spaceId={spaceId}>
      {/* GHE #597 — owns the ONE TaskCreateDialog for this route and lets any
          surface below (the chrome button today; board columns, calendar cells
          and the empty states in later slices) open it with context. Inside
          ListScopeProvider so both contexts are available together. */}
      <ListTaskCreateProvider
        listId={listId ?? ''}
        statuses={statuses}
        // GHE #883 — owner: "if they can't create a task they should directly not see the
        // buttons that they cannot use". `canWriteSpace` is not enough on its own since #873:
        // creating a task requires a client, and a client requires being a manager of this area
        // or the RT of something. `canCreateTask` is the server's own answer for THIS list,
        // resolved by the same predicate the write uses — so a hidden button and a 403 can never
        // disagree. Defaults to false while the list is still loading: briefly missing is
        // better than briefly offering something that refuses.
        canCreate={canWriteSpace && Boolean(listId) && (listData?.data?.canCreateTask ?? false)}
      >
        <div className="flex flex-col">
          {/* 🚨 Un `h1` de verdad, aunque no se vea (GHE #1186).
              La auditoría encontró que las SEIS superficies donde se trabaja a diario —las
              cinco vistas de una lista y el detalle de tarea— no tenían NI UN encabezado,
              mientras que las 17 sub-vistas de Administración y Configuración lo tenían todas.
              La disciplina existe y se caía justo donde más se usa.

              Apple §16, orientación: toda pantalla debe contestar «¿dónde estoy?». Aquí lo
              contestaba sólo la miga de pan, que es navegación y no un encabezado: quien usa
              un lector de pantalla no tiene forma de saltar al título de la página porque no
              hay título.

              Va `sr-only` y no visible a propósito: el nombre de la lista YA está en pantalla,
              al final de la miga. Duplicarlo visualmente sería ruido; lo que faltaba era la
              semántica, no el píxel. */}
          <h1 className="sr-only">{listName}</h1>

          {/* Breadcrumb */}
          <Breadcrumb segments={breadcrumbSegments} />

          {/* View switcher — viewMode is the URL search param (shareable, GHE #284 item 1),
            seeded from this list's persisted localStorage preference when absent. */}
          {/* GHE #701 — screen-only. The switcher and the list-scoped actions are navigation, not
            content: a client receiving this PDF has no use for "Nueva tarea" or a view picker.
            Marked on the CONTAINER so all five views inherit it from this one place. */}
          {/* 🚨 `flex-wrap` — a 390px esta fila empujaba la PÁGINA 48px fuera del viewport
              (GHE #1186). Ni envolvía (`nowrap` por defecto) ni tenía scroll propio, así que
              el desbordamiento salía por arriba y el cuerpo entero se desplazaba en
              horizontal, que es lo que la regla prohíbe: lo ancho scrollea DENTRO de su
              contenedor.

              Envolver y no `overflow-x-auto` a propósito: con `justify-between` un
              contenedor desplazable deja los botones de acción fuera de vista y sin nada que
              indique que están ahí. Envolviendo, el conmutador se queda en la primera línea y
              las acciones bajan a la segunda — todo visible y en un orden que se entiende.

              En escritorio no cambia nada: `flex-wrap` sólo actúa cuando el contenido no
              cabe. */}
          <div
            data-print-hide
            className="flex flex-wrap items-center justify-between gap-y-2 border-b border-border pb-2"
          >
            <ViewSwitcher activeView={viewMode} onSelect={setViewMode} />
            {/* GHE #597 — the list-scoped actions are clustered so the row stays
              [switcher] ......... [+ Nueva tarea] [import] [trash]. Without the
              wrapper, `justify-between` spreads four siblings evenly and the
              create button drifts into the middle of the row. */}
            <div className="flex items-center gap-1">
              {/* The one always-present create affordance (GHE #597). Above the
                Suspense boundary, so it is there in every view and every state —
                including Table, which had no create path at all. */}
              <CreateTaskButton />
              {/* Export the current view with its live filters (GHE #687). Deliberately NOT
                gated on `canManageSpace` like the two icons below: exporting is a READ, and the
                endpoint re-derives its own scope from the caller's access predicate server-side.
                Manage-gating a read is how nine surfaces were emptied for VIEWERs before — the
                rule is that a person may export exactly what they can already see on screen. */}
              {/* GHE #687 — `filterHook.columns` es la MISMA fuente que alimenta la tabla
                  (`chosenColumns`, más abajo). Pasarla aquí es lo que hace que el fichero y la
                  pantalla no puedan discrepar: si se leyera de otro sitio, serían dos respuestas
                  a la misma pregunta. `null` —nadie ha tocado el selector— manda el conjunto de
                  siempre. */}
              {listId && (
                <TaskExportMenu listId={listId} filters={apiParams} columns={filterHook.columns} />
              )}
              {/* GHE #1263 — al lado del de exportar y por la misma razón: los dos actúan sobre lo
                  que se está viendo AHORA, y los dos reciben `apiParams` sin tocar. Sin gate de
                  manage: una alerta sólo puede avisarte de lo que ya puedes ver, y el servidor
                  re-comprueba el acceso cada vez que la evalúa. */}
              {listId && <CreateAlertButton listId={listId} filters={apiParams} />}
              {/* Bulk import from Dataverse (#347) — sits beside the trash for the same reason:
              a list-scoped action that belongs to the list, not to any one task. Gated on
              manage like the other list-scoped affordance here; the route itself asserts
              `write` on the space, so this is the narrower of the two. */}
              {canManageSpace && listId && (
                <button
                  type="button"
                  onClick={() => setDataverseImportOpen(true)}
                  title={t('dataverseBulk.triggerTooltip')}
                  aria-label={t('dataverseBulk.triggerAria')}
                  /* The Dataverse accent, not the toolbar's neutral grey: this is the one
                     control in the row that leaves Plani. Icon-only, so the colour carries no
                     information the label does not also carry — 5.27:1 against the surface,
                     comfortably past the 3:1 WCAG 1.4.11 asks of a non-text control. */
                  className="flex h-9 w-9 coarse:min-h-[44px] coarse:min-w-[44px] shrink-0 items-center justify-center rounded-md text-[var(--color-dataverse)] transition-colors hover:bg-surface-overlay focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
                >
                  <Link2 className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
              {/* Recently-deleted / trash trigger (#95 cluster C) — manage-tier only, matching
              the deleted-tasks endpoint's own MANAGE gate. */}
              {canManageSpace && (
                <button
                  type="button"
                  onClick={() => setRecentlyDeletedOpen(true)}
                  title={t('recentlyDeleted.triggerTooltip')}
                  aria-label={t('recentlyDeleted.triggerAria')}
                  className="flex h-9 w-9 coarse:min-h-[44px] coarse:min-w-[44px] shrink-0 items-center justify-center rounded-md text-[var(--text-secondary)] transition-colors hover:bg-surface-overlay hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
                >
                  <History className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          {/* Filter bar — sits between ViewSwitcher and the active view.
            All 5 views inherit filter params via apiParams.
            showGroupBy is only true for the List view since only that view
            groups tasks; Board is always kanban-by-status, Gantt has its own. */}
          {/* GHE #701 — screen-only: the filter CONTROLS are chrome. Their EFFECT is not — the
            printed page shows the filtered set, which is the point. */}
          <div data-print-hide>
            <TaskFilterBar
              listId={listId ?? ''}
              statuses={statuses}
              filterHook={filterHook}
              showGroupBy={viewMode === 'list'}
              // Sólo estas dos vistas tienen columnas que elegir.
              showColumns={viewMode === 'list' || viewMode === 'table'}
              // Se ofrece sólo cuando puede hacer algo: con un filtro de estado explícito el
              // usuario ya está eligiendo lo que ve, y un segundo control diciendo que esconde
              // lo terminado contradiría a la propia barra.
              finishedWork={
                viewMode === 'list' && !hasExplicitStatusFilter(apiParams)
                  ? { showDone, onToggle: () => setShowDone((v) => !v) }
                  : undefined
              }
            />
          </div>

          {/* Active view — lazy views are wrapped in Suspense.
            apiParams is passed to each view so they include it in their queryKey
            and fetch calls, causing a refetch whenever filters change. */}
          <Suspense fallback={<ViewLoadingFallback />}>
            {viewMode === 'list' && (
              <ListView
                chosenColumns={filterHook.columns}
                showDone={showDone}
                listId={listId ?? ''}
                apiParams={apiParams}
                groupBy={groupBy}
                initialOpenTaskId={search.openTask}
                initialCommentId={search.comment}
              />
            )}
            {viewMode === 'board' && <BoardView listId={listId ?? ''} apiParams={apiParams} />}
            {viewMode === 'table' && (
              <TableView
                listId={listId ?? ''}
                apiParams={apiParams}
                chosenColumns={filterHook.columns}
              />
            )}
            {viewMode === 'gantt' && <GanttView listId={listId ?? ''} apiParams={apiParams} />}
            {viewMode === 'calendar' && (
              <CalendarView listId={listId ?? ''} apiParams={apiParams} />
            )}
          </Suspense>

          {/* Route-level deep-link panel (GHE #106 item 2) — covers every view EXCEPT List,
            which already opens/closes its own instance internally (see the capture-effect
            comment above for why gating on `viewMode` here is sufficient post-#284). */}
          {viewMode !== 'list' && routeDeepLinkTaskId && (
            <TaskDetailPanel
              taskId={routeDeepLinkTaskId}
              statuses={statuses}
              onClose={handleCloseRouteDeepLinkPanel}
              initialCommentId={routeDeepLinkCommentId ?? undefined}
            />
          )}

          {recentlyDeletedOpen && listId && (
            <RecentlyDeletedDialog listId={listId} onClose={() => setRecentlyDeletedOpen(false)} />
          )}

          {/* Bulk import from Dataverse (#347) — mounted only while open so the row search does
            not fire for every list anybody visits. */}
          {dataverseImportOpen && listId && (
            <DataverseTaskBulkImportDialog
              listId={listId}
              onClose={() => setDataverseImportOpen(false)}
            />
          )}
        </div>
      </ListTaskCreateProvider>
    </ListScopeProvider>
  );
}
